import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button, Typography } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * 공통 에러 바운더리 컴포넌트
 * 
 * 이 컴포넌트는 자식 컴포넌트에서 발생하는 JavaScript 에러를 캐치하고
 * 에러 UI를 표시합니다.
 * 
 * @param children - 자식 컴포넌트들
 * @param fallback - 커스텀 에러 UI (선택사항)
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // 에러가 발생하면 상태를 업데이트하여 다음 렌더링에서 에러 UI를 표시
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 개발 환경에서만 에러 로깅
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }
    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // 커스텀 에러 UI가 있으면 사용
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 기본 에러 UI
      return (
        <Result
          status="error"
          title="오류가 발생했습니다"
          subTitle="페이지를 새로고침하거나 홈으로 돌아가주세요."
          extra={[
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={this.handleReload}
              key="reload"
            >
              새로고침
            </Button>,
            <Button 
              icon={<HomeOutlined />} 
              onClick={this.handleGoHome}
              key="home"
            >
              홈으로
            </Button>,
          ]}
        >
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <div style={{ marginTop: 24, textAlign: 'left' }}>
              <Text type="secondary">
                <strong>에러 정보 (개발 모드):</strong>
              </Text>
              <pre style={{ 
                marginTop: 8, 
                padding: 12, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 4,
                fontSize: 12,
                overflow: 'auto'
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo && `\n\n${this.state.errorInfo.componentStack}`}
              </pre>
            </div>
          )}
        </Result>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 